import { useMemo } from 'react';
import {
  generateSlots,
  groupSlotsByDay,
  dayStatus,
  type SlotConfig,
  type TimeSlot,
} from '../lib/slots';

/**
 * Sélecteur de créneaux (étape 2 — parité legacy).
 * Remplace le champ texte/`<input type="time">` : les créneaux sont GÉNÉRÉS
 * depuis `opening_hours` du tenant (pas, prép, après-minuit, fermetures, tri,
 * capacité). Groupés par jour, avec bandeau de statut du jour courant.
 */
interface SlotPickerProps {
  config: SlotConfig;
  /** Créneau sélectionné (par id). */
  value: TimeSlot | null;
  onSelect: (slot: TimeSlot) => void;
  /** Téléphone affiché dans le message « aucun créneau ». */
  phone?: string | null;
  /** Titre de la section (parité legacy §F-20 : « ou de livraison » si livraison activée). */
  title?: string;
}

export default function SlotPicker({
  config,
  value,
  onSelect,
  phone,
  title = 'Heure de retrait',
}: SlotPickerProps) {
  // `config` est recréé à chaque rendu par le parent ; on dépend de ses champs
  // sérialisables pour ne recalculer que si les horaires changent réellement.
  const depKey = useMemo(
    () =>
      JSON.stringify({
        h: config.hours,
        s: config.stepMin,
        p: config.prepMin,
        c: config.capacity,
        cl: config.closures,
        b: config.booked,
        n: config.now?.getTime() ?? null,
      }),
    [config],
  );

  const slots = useMemo(() => generateSlots(config), [depKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => groupSlotsByDay(slots), [slots]);
  const status = useMemo(() => dayStatus(config), [depKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstAvailableDay = groups.length > 0 ? groups[0].label : null;

  if (slots.length === 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Aucun créneau disponible dans les prochains jours.
        {phone ? ` Appelez le restaurant au ${phone}.` : ''}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs opacity-60">Choisissez votre créneau.</p>
      </div>

      {status.type !== 'open' && status.msg && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          <div className="font-semibold">{status.msg}</div>
          {firstAvailableDay && (
            <div className="mt-1">
              Premier créneau disponible : <b>{firstAvailableDay}</b>.
            </div>
          )}
        </div>
      )}

      {groups.map((group, gi) => (
        <div key={group.dayKey}>
          <div className="mb-2 flex items-baseline gap-2">
            <h4
              className="font-bold capitalize"
              style={gi === 0 ? { color: 'var(--brand-primary)' } : {}}
            >
              {group.label}
            </h4>
            <span className="text-xs opacity-50">
              {group.slots.length} créneau{group.slots.length > 1 ? 'x' : ''}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {group.slots.map((s) => {
              const selected = value?.id === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={s.taken}
                  onClick={() => !s.taken && onSelect(s)}
                  className={
                    'rounded-lg border-2 py-2 text-sm font-semibold transition ' +
                    (selected
                      ? 'text-white'
                      : s.taken
                        ? 'border-slate-200 bg-slate-50 text-slate-300'
                        : 'border-slate-200 bg-white hover:border-slate-400')
                  }
                  style={
                    selected
                      ? { background: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' }
                      : {}
                  }
                >
                  <div>{s.label}</div>
                  {s.taken && <div className="text-[10px] font-normal">Complet</div>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
